import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Updates count + total on the fixed cart indicator.
 * Drawer open is handled in Liquid via on:click="#cart-drawer/toggle".
 */
class SepetGosterici extends HTMLElement {
  /** @type {HTMLElement | null} */
  #countEl = null;

  /** @type {HTMLElement | null} */
  #priceEl = null;

  /** @type {HTMLElement | null} */
  #priceMobileEl = null;

  /** @type {HTMLElement | null} */
  #button = null;

  /** @type {string} */
  #moneyFormat = '{{amount}}';

  /** @type {string} */
  #currency = 'TRY';

  /** @type {number} */
  #lastCount = 0;

  /** @type {number | undefined} */
  #bumpTimer;

  connectedCallback() {
    // Keep fixed pill relative to the viewport on iOS (avoid page-wrapper quirks)
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.#countEl = this.querySelector('[data-sepet-adet]');
    this.#priceEl = this.querySelector('[data-sepet-fiyat]');
    this.#priceMobileEl = this.querySelector('[data-sepet-fiyat-mobile]');
    this.#button = this.querySelector('.sepet_gosterici');
    this.#moneyFormat = this.dataset.moneyFormat || this.#moneyFormat;
    this.#currency = this.dataset.currency || this.#currency;
    this.#lastCount = Number(this.#countEl?.textContent?.match(/\d+/)?.[0] || 0);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    document.addEventListener('theme-drawer:open', this.#onDrawerOpen);
    document.addEventListener('theme-drawer:close', this.#onDrawerClose);
    this.#syncDrawerVisibility();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    document.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
    document.removeEventListener('theme-drawer:close', this.#onDrawerClose);
    window.clearTimeout(this.#bumpTimer);
  }

  #onDrawerOpen = (event) => {
    const drawer = event.target;
    if (drawer instanceof HTMLElement && drawer.id === 'cart-drawer') {
      this.setAttribute('data-cart-open', '');
    }
  };

  #onDrawerClose = () => {
    this.#syncDrawerVisibility();
  };

  #syncDrawerVisibility() {
    const cartOpen = Boolean(document.querySelector('theme-drawer#cart-drawer[open]'));
    this.toggleAttribute('data-cart-open', cartOpen);
  }

  /**
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#refreshFromCartJs();
  };

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(async ({ cart, detail }) => {
        const count = Number(cart?.totalQuantity ?? detail?.itemCount ?? 0);
        const cents = this.#extractCents(cart, detail);

        if (cents == null) {
          await this.#refreshFromCartJs();
          return;
        }

        this.#apply(count, cents);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[sepet-gosterici] cart update failed:', error);
        }
      });
  };

  /**
   * @param {object | undefined} cart
   * @param {object | undefined} detail
   * @returns {number | null}
   */
  #extractCents(cart, detail) {
    const costAmount = cart?.cost?.totalAmount?.amount;
    if (costAmount != null && costAmount !== '') {
      const parsed = Number(costAmount);
      if (!Number.isNaN(parsed)) {
        // Standard events often expose major units as a string ("16.70")
        if (typeof costAmount === 'string' && costAmount.includes('.')) {
          return Math.round(parsed * 100);
        }
        // Sometimes already minor units
        if (Number.isInteger(parsed) && parsed >= 100) return parsed;
        return Math.round(parsed * 100);
      }
    }

    if (typeof detail?.total_price === 'number') return detail.total_price;
    if (typeof cart?.total_price === 'number') return cart.total_price;

    return null;
  }

  async #refreshFromCartJs() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return;
      const cart = await response.json();
      this.#apply(Number(cart.item_count || 0), Number(cart.total_price || 0));
    } catch (error) {
      console.warn('[sepet-gosterici] cart.js fetch failed:', error);
    }
  }

  /**
   * @param {number} count
   * @param {number} cents
   */
  #apply(count, cents) {
    const empty = count <= 0;
    const shouldBump = count > this.#lastCount && count > 0;
    this.#lastCount = count;

    this.classList.toggle('is-empty', empty);
    this.toggleAttribute('hidden', empty);

    if (this.#button instanceof HTMLElement) {
      this.#button.setAttribute('aria-hidden', empty ? 'true' : 'false');
    }

    if (this.#countEl) {
      this.#countEl.textContent = `${count} ürün`;
    }

    const formatted = formatMoney(cents, this.#moneyFormat, this.#currency);
    if (this.#priceEl) {
      this.#priceEl.textContent = formatted;
    }
    if (this.#priceMobileEl) {
      this.#priceMobileEl.textContent = formatted;
    }

    if (shouldBump) this.#bump();
  }

  #bump() {
    const target = this.#button;
    if (!(target instanceof HTMLElement)) return;

    target.classList.remove('is-bump');
    // Retrigger animation if already playing
    void target.offsetWidth;
    target.classList.add('is-bump');

    window.clearTimeout(this.#bumpTimer);
    this.#bumpTimer = window.setTimeout(() => {
      target.classList.remove('is-bump');
    }, 650);
  }
}

if (!customElements.get('sepet-gosterici')) {
  customElements.define('sepet-gosterici', SepetGosterici);
}
